/**
 * Internal notes, and client updates.
 *
 * These are SEPARATE RESOURCES with separate permission modules — `note` and
 * `update` — not one resource with a visibility flag. The backend registers them as
 * two viewsets for exactly that reason: a single `cases/{id}/entries` with a
 * visibility field is the shape that leaks (CR-25).
 *
 * The interface keeps them separate too, and labels both. An internal note about a
 * counterparty's settlement posture and a client update saying "we have filed your
 * defence" must never be one keystroke apart in an undifferentiated list.
 */

import {
  qk,
  useApi,
  useApiMutation,
  usePaginatedList,
  type CaseNote,
  type CaseUpdate,
} from '@legalcrm/api';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Textarea,
  useToast,
} from '@legalcrm/ui';
import { useState } from 'react';

import { STAFF } from '@legalcrm/api';
import { Can, useCan } from '@/auth/permissions';
import { formatDateTime } from '@/lib/format';

export function CaseNotesTab({ caseId }: { caseId: string }) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <NotesPanel caseId={caseId} />
      <UpdatesPanel caseId={caseId} />
    </div>
  );
}

function NotesPanel({ caseId }: { caseId: string }) {
  const api = useApi();
  const { toast } = useToast();
  const [body, setBody] = useState('');
  const canCreate = useCan('note', 'create');

  const list = usePaginatedList<CaseNote>(
    qk.sub('cases', caseId, 'notes'),
    STAFF.caseNotes(caseId),
  );

  const create = useApiMutation<CaseNote, { body: string }>(
    {
      mutationFn: (payload) => api.post<CaseNote>(STAFF.caseNotes(caseId), payload),
      invalidates: [qk.sub('cases', caseId, 'notes')],
    },
    {
      onSuccess: () => {
        setBody('');
        toast({ title: 'Note added', tone: 'success' });
      },
    },
  );

  return (
    // `sensitivity` renders a persistent "Internal only" band. Colour alone is not
    // sufficient to distinguish internal from client-facing content.
    <Card sensitivity="internal">
      <CardHeader
        title="Internal notes"
        description="Never shown to the client, and not part of the client timeline."
      />

      {canCreate && (
        <CardBody className="border-b">
          <Textarea
            label="Add a note"
            rows={3}
            value={body}
            maxLength={4000}
            showCount
            onChange={(event) => setBody(event.target.value)}
            error={create.error ? 'The note could not be saved.' : undefined}
          />
          <div className="mt-2 flex justify-end">
            <Button
              variant="primary"
              size="sm"
              disabled={!body.trim()}
              loading={create.isPending}
              onClick={() => create.mutate({ body: body.trim() })}
            >
              Add note
            </Button>
          </div>
        </CardBody>
      )}

      {list.error && <ErrorState error={list.error} size="sm" onRetry={() => void list.refetch()} />}

      {!list.error && list.items.length === 0 && !list.isLoading && (
        <EmptyState size="sm" title="No notes yet" />
      )}

      <ul className="flex flex-col divide-y">
        {list.items.map((note) => (
          <li key={note.id} className="px-4 py-3">
            <p className="whitespace-pre-line text-base text-ink">{note.body}</p>
            <p className="mt-1 text-xs text-ink-muted">
              {note.author_name} · {formatDateTime(note.created_at)}
            </p>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function UpdatesPanel({ caseId }: { caseId: string }) {
  const api = useApi();
  const { toast } = useToast();
  const [body, setBody] = useState('');

  const list = usePaginatedList<CaseUpdate>(
    qk.sub('cases', caseId, 'updates'),
    STAFF.caseUpdates(caseId),
  );

  const create = useApiMutation<CaseUpdate, { body: string }>(
    {
      mutationFn: (payload) => api.post<CaseUpdate>(STAFF.caseUpdates(caseId), payload),
      invalidates: [qk.sub('cases', caseId, 'updates')],
    },
    {
      onSuccess: () => {
        setBody('');
        toast({
          title: 'Draft saved',
          description: 'The client cannot see it until it is published.',
          tone: 'success',
        });
      },
    },
  );

  const publish = useApiMutation<CaseUpdate, string>(
    {
      mutationFn: (updateId) =>
        api.post<CaseUpdate>(STAFF.caseUpdatePublish(caseId, updateId)),
      invalidates: [qk.sub('cases', caseId, 'updates'), qk.sub('cases', caseId, 'timeline')],
    },
    {
      onSuccess: () => toast({ title: 'Update published to the client', tone: 'success' }),
    },
  );

  return (
    <Card sensitivity="client-facing">
      <CardHeader
        title="Client updates"
        description="Drafted here, visible to the client only once published."
      />

      <Can module="update" action="create">
        <CardBody className="border-b">
          <Textarea
            label="Write an update"
            hint="Plain language. The client reads this exactly as written."
            rows={3}
            value={body}
            maxLength={4000}
            showCount
            onChange={(event) => setBody(event.target.value)}
          />
          <div className="mt-2 flex justify-end">
            <Button
              variant="primary"
              size="sm"
              disabled={!body.trim()}
              loading={create.isPending}
              onClick={() => create.mutate({ body: body.trim() })}
            >
              Save draft
            </Button>
          </div>
        </CardBody>
      </Can>

      {list.error && <ErrorState error={list.error} size="sm" onRetry={() => void list.refetch()} />}

      {!list.error && list.items.length === 0 && !list.isLoading && (
        <EmptyState size="sm" title="No updates yet" />
      )}

      <ul className="flex flex-col divide-y">
        {list.items.map((update) => (
          <li key={update.id} className="px-4 py-3">
            <div className="flex items-start justify-between gap-2">
              <p className="whitespace-pre-line text-base text-ink">{update.body}</p>
              <Badge tone={update.is_published ? 'client-facing' : 'neutral'} size="sm">
                {update.is_published ? 'Published' : 'Draft'}
              </Badge>
            </div>
            <div className="mt-1 flex items-center justify-between gap-2">
              <p className="text-xs text-ink-muted">
                {update.author_name} · {formatDateTime(update.created_at)}
              </p>
              {/* Publishing is a distinct, permissioned action — `update.publish` —
                  because it is the moment the content becomes client-visible. */}
              {!update.is_published && (
                <Can module="update" action="publish">
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={publish.isPending && publish.variables === update.id}
                    onClick={() => publish.mutate(update.id)}
                  >
                    Publish
                  </Button>
                </Can>
              )}
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}
